import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { Location } from '@angular/common';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { GbgemeindeService } from '../../../core/gb-api';
import { switchMap } from 'rxjs/operators';
import {
  BauprojektDetailEntry,
  BauprojektSearchByIdResponse,
  GwrbauprojektService,
  GwrGebaeudeDetailEntry,
  GwrGebaeudeListEntry,
  GwrGrundstueckListEntry
} from '../../../core/gwr-api';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { CapAlertLevel } from '../../../core/extension/exceptions/cap-alert-level.enum';
import { GwrStatus } from '../../../shared/enum/GwrStatus';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { KopfdatenelementComponent } from '../../../shared/widgets/kopfdatenelement/kopfdatenelement.component';
import { GwrGebaeudeTableComponent } from '../../gwr-gebaeude/gwr-gebaeude-table/gwr-gebaeude-table.component';
import { GwrGrundstueckTableComponent } from '../../gwr-grundstueck-list/gwr-grundstueck-table.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { DatumPipe } from '../../../shared/pipes/simple/datum.pipe';
import { SubstringPipe } from '../../../shared/pipes/simple/substring.pipe';
import BauwerkeTypEnum = BauprojektDetailEntry.BauwerkeTypEnum;
import BauwerkeArtEnum = BauprojektDetailEntry.BauwerkeArtEnum;
import GebaeudeKategorieEnum = GwrGebaeudeDetailEntry.GebaeudeKategorieEnum;

@Component({
  selector: 'cap-gwr-bauprojekt-detail',
  templateUrl: './gwr-bauprojekt-detail.component.html',
  styleUrls: ['./gwr-bauprojekt-detail.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    KopfdatenelementComponent,
    GwrGebaeudeTableComponent,
    GwrGrundstueckTableComponent,
    CapSpinnerSmallComponent,
    TranslatePipe,
    DatumPipe,
    SubstringPipe
  ]
})
export class GwrBauprojektDetailComponent extends AbstractComponent implements OnInit {
  bauprojekt: BauprojektDetailEntry;

  grundstueckListLoader: ListLoader<GwrGrundstueckListEntry>;

  gebaeudeListLoader: ListLoader<GwrGebaeudeListEntry>;

  constructor(
    private bauprojektService: GwrbauprojektService,
    private stateService: SearchStateService,
    private gemeindeService: GbgemeindeService,
    protected router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private location: Location,
    protected comparator: ComparatorService,
    private capAlertService: CapAlertService
  ) {
    super();
    keysToTranslate<string>([
      'TypDerBauwerke',
      'ArtDerBauwerke',
      'ArtDerArbeiten',
      'DatumProjekteingabe',
      'DatumBaubeginn',
      'DatumBaubewilligung',
      'DatumBauende',
      'VoraussichtlicheBaudauer',
      'AnzahlGebaeude',
      'AnzahlWohnungen',
      'WeitereBauprojektdaten',
      'GWRBauprojekt',
      'ProjektierteGebaeudeWohnungen',
      'DetailsZuGrundstueckAusSichtGrundbuch',
      'DetailsZuGwrGebäude',
      'NEUBAU',
      'UMBAU',
      'ABBRUCH'
    ]);
    keysToTranslate<BauwerkeArtEnum | BauwerkeTypEnum | GebaeudeKategorieEnum>([
      'WASSERVERSORGUNGSANLAGEN',
      'ELEKTRIZITAETSWERKE_NETZE',
      'GASWERKE_NETZE_CHEMISCHE_ANLAGEN',
      'FERNHEIZUNGSANLAGEN',
      'UEBRIGE_VERSORGUNGSANLAGEN',
      'WASSERENTSORGUNGSANLAGEN',
      'KEHRICHTENTSORGUNGSANLAGEN',
      'UEBRIGE_ENTSORGUNGSANLAGEN',
      'NATIONALSTRASSEN',
      'KANTONSSTRASSEN',
      'GEMEINDESTRASSEN',
      'UEBRIGER_STRASSENBAU_PARKPLAETZE',
      'PARKHAEUSER',
      'BAHNANLAGEN',
      'BUS_TRAMANLAGEN',
      'SCHIFFSVERKEHRSANLAGEN',
      'FLUGVERKEHRSANLAGEN',
      'KOMMUNIKATIONSANLAGEN',
      'UEBRIGE_VERKEHRSANLAGEN',
      'SCHULEN_BILDUNGSWESEN_BIS_MATURASTUFE',
      'HOEHERES_BILDUNGSWESEN_FORSCHUNG',
      'AKUTSPITAELER_ALLGEMEINE_SPITAELER',
      'HEIME_MIT_UNTERKUNFT_PFLEGEDIENSTE_BETREUUNG',
      'UEBRIGES_SPEZIALISIERTES_GESUNDHEITSWESEN',
      'FREIZEIT_TOURISMUSANLAGEN',
      'KIRCHEN_SAKRALBAUTEN',
      'KULTURBAUTEN_MUSEEN_BIBLIOTHEKEN_DENKMAELER',
      'SPORTHALLEN_SPORTPLAETZE',
      'UFERVERBAUUNGEN_STAUDAEMME',
      'LANDESVERTEIDIGUNGSBAUTEN',
      'UEBRIGE_INFRASTRUKTUR',
      'EINFAMILIENHAEUSER_FREISTEHEND',
      'EINFAMILIENHAEUSER_ANGEBAUT',
      'MEHRFAMILIENHAEUSER_REINE_WOHNGEBAEUDE_MIT_MEHREREN_WOHNUNGEN',
      'WOHNGEBAEUDE_MIT_NEBENNUTZUNG_BAUERNHAEUSER',
      'WOHNHEIME_OHNE_PFLEGEDIENSTE_BETREUUNG',
      'GARAGEN_PARKPLAETZE_EINSTELLHALLEN_IM_ZUSAMMENHANG_MIT_WOHNGEBAEUDEN',
      'UEBRIGE_BAUTEN_IM_ZUSAMMENHANG_MIT_WOHNGEBAEUDEN',
      'LANDWIRTSCHAFTSBAUTEN',
      'FORSTWIRTSCHAFTSBAUTEN',
      'MELIORATIONEN',
      'WERKSTAETTEN_FABRIKGEBAEUDE',
      'LAGERHALLEN_DEPOTS_SILOS_ZISTERNEN',
      'BUEROGEBAEUDE_VERWALTUNGSGEBAEUDE',
      'KAUFHAEUSER_GESCHAEFTSGEBAEUDE',
      'HOTELS_RESTAURANTS',
      'ANDERE_BEHERBERGUNGEN',
      'UEBRIGE_VERWENDUNG_FUER_WIRTSCHAFTLICHE_ZWECKE',
      'TIEFBAU',
      'HOCHBAU',
      'SONDERBAU',
      'PROVISORISCHE_UNTERKUNFT',
      'GEBAEUDE_MIT_AUSSCHLIESSLICHER_WOHNNUTZUNG',
      'MEHRFAMILIENHAEUSER',
      'EINFAMILIENHAEUSER',
      'ANDERE_WOHNGEBAEUDE',
      'GEBAEUDE_MIT_TEILWEISER_WOHNNUTZUNG',
      'GEBAEUDE_OHNE_WOHNNUTZUNG',
      'SONDERBAU'
    ]);
  }

  ngOnInit() {
    this.route.data.subscribe((data) => {
      if (data.origin === 'eproid') {
        this.addSub(
          this.route.paramMap
            .pipe(
              switchMap((params: ParamMap) => {
                const requestParam = params.get('eproid');
                if (requestParam) {
                  return this.bauprojektService.loadBauprojektByEproid(+requestParam);
                }
              })
            )
            .subscribe({
              next: (entry) => this.fillBauprojektFrom(entry),
              error: (err) => this.handleError(err)
            })
        );
      } else if (data.origin === 'dossiernummer') {
        this.addSub(
          this.route.queryParams
            .pipe(
              switchMap((param) => {
                const requestParam = param['request'];
                if (requestParam) {
                  const request = JSON.parse(requestParam);
                  return this.bauprojektService.searchBauprojektDossiernummer(request);
                }
              })
            )
            .subscribe({
              next: (entry) => this.fillBauprojektFrom(entry),
              error: (err) => this.handleError(err)
            })
        );
      }
    });
  }

  private fillBauprojektFrom(response: BauprojektSearchByIdResponse) {
    const wsStatus = response.gwrStatusCode;
    if (wsStatus.statusCode !== GwrStatus.OK) {
      const alertText = this.translate.instant('FehlerBeimAufrufDesWebservice');
      this.capAlertService.warning(`${alertText}: ${wsStatus.statusCode} (${wsStatus.text})`, CapAlertLevel.CONTENT_HEADER, true);
      this.navigateBackToSearch();
    } else {
      this.bauprojekt = response.result;
      this.initGrundstueckListLoader();
      this.initGebaeudeListLoader();
    }
  }

  private initGebaeudeListLoader() {
    this.gebaeudeListLoader = new ListLoaderPreloaded<GwrGebaeudeListEntry>(15, 1, this.bauprojekt.gebaeude);
    this.addLoader(this.gebaeudeListLoader);
  }

  initGrundstueckListLoader() {
    this.grundstueckListLoader = new ListLoaderPreloaded<GwrGrundstueckListEntry>(15, 1, this.bauprojekt.grundstuecke);
    this.addLoader(this.grundstueckListLoader);
  }

  navigateBackToSearch() {
    this.location.back();
  }

  private handleError(err) {
    if (err.status === 400) {
      this.location.back();
    }
    throw err;
  }

  public get monate() {
    if (this.bauprojekt.baudauer > 1) {
      return ' ' + this.translate.instant('monate');
    }
    if (this.bauprojekt.baudauer === 1) {
      return ' ' + this.translate.instant('monat');
    }
    return '';
  }
}
